<script setup lang="ts">
import type { DailyCheckinRewardView } from '~/types/daily-checkin';
import { getAssetsUrlForCDN } from '@/utils/template';

// Popup do degrau — frame "Resgate" do Figma (41:2033), 250px de largura:
//   Resgatar (41:2032)          → azul, manchete + arte 160 + botão RESGATAR
//   Prop3 (198:4874)            → navy, sem botão (degrau ainda não alcançado)
//   Resgatado State 01 (41:2034)→ verde, arte + check + botão USAR
//
// A variante Prop4 (arte menor + nome + descrição no corpo) foi descartada: a
// ficha do prêmio virou o popover do (i), igual ao da tela do calendário.
//
// Vive num overlay próprio (z-9997) em vez de dentro do painel do calendário:
// o painel tem `overflow: clip` e recortaria o popup.
const props = defineProps<{
  reward: DailyCheckinRewardView | null;
  // Resgate em voo: trava o botão.
  claiming?: boolean;
  // Mensagem de recusa já resolvida pela store (por código de erro).
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'claim'): void;
  // Destino JÁ resolvido (contrato ou fallback). Quem navega é o modal: ele é
  // que sabe fechar o painel antes de trocar de rota.
  (e: 'use', link: string): void;
}>();

// Destino do USAR quando o contrato não fala do assunto: a tela de recompensas
// da conta. Todo prêmio da trilha é creditado como recompensa, então ela é o
// lugar onde qualquer um deles pode ser visto — é o fallback que nunca aponta
// pro prêmio errado.
const REWARDS_FALLBACK = '/user/rewards';

// `visible` mantém o overlay montado até a animação de saída terminar.
const visible = ref(false);
const shown = ref(false);

// Ficha do prêmio (nome + descrição) num popover ancorado no (i), com a mesma
// barriguinha e o mesmo clique-fora do popover da tela do calendário.
//
// Diverge da variante Prop4 do Figma, que trocava o corpo do card (arte menor
// + nome + descrição no lugar da manchete): decisão do Lucas de padronizar as
// duas telas no mesmo dropdown. Por isso a ficha some do corpo e o card fica
// sempre com manchete + arte grande, em todos os tons.
const infoOpen = ref(false);

const isClaimed = computed(() => props.reward?.state === 'claimed');
const isClaimable = computed(() => props.reward?.state === 'claimable');

// Dois tons e o azul de todo o resto. Existiu um terceiro, vermelho, pro degrau
// fora de alcance — saiu junto com o vermelho da célula: o popup nunca contradiz
// a cor da trilha que o abriu.
const tone = computed(() => {
  if (isClaimed.value) return 'claimed';
  if (isClaimable.value) return 'claimable';

  return 'locked';
});

const headline = computed(() => props.reward?.title ?? '');

const prizeImage = computed(() =>
  props.reward ? getAssetsUrlForCDN(props.reward.imageUrl) : '',
);

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/daily-checkin/${file}`);

// Os ícones do tom azul e do navy são os mesmos do calendário (idênticos byte
// a byte no export); só o verde tem par próprio. O ícone não muda com o
// popover aberto — o olho da variante Prop4 do Figma foi descartado.
const infoIcon = computed(() =>
  isClaimed.value ? asset('popup/info-green.svg') : asset('info.svg'),
);

const closeIcon = computed(() =>
  isClaimed.value ? asset('popup/close-green.svg') : asset('close.svg'),
);

// Agora o (i) existe em todos os tons: com a ficha no dropdown, sem ele o
// navy não teria como mostrar a descrição.
const hasInfo = computed(() => !!props.reward?.description);

// Destino do botão USAR, resolvido pelo tri-estado do contrato (ver
// DailyCheckinReward.ctaUrl):
//
//   chave ausente → /user/rewards (o core ainda não manda destino)
//   chave vazia   → null, e sem destino não há botão
//   com valor     → o destino cadastrado
//
// `undefined` também é o que se lê com o popup fechado (reward null), e aí o
// valor não é consultado — nada renderiza.
// (`ctaLink` e não `useLink`: esse nome é de um composable do vue-router que o
// Nuxt auto-importa, e um `const` local por cima dele confunde mais do que
// economiza.)
const ctaLink = computed(() => {
  const raw = props.reward?.ctaUrl;

  if (raw === undefined) return REWARDS_FALLBACK;

  return raw || null;
});

// O USAR só existe no degrau resgatado: é o prêmio que já está na conta. Nos
// outros tons não há o que usar.
const hasUse = computed(() => isClaimed.value && !!ctaLink.value);

/* ---------------- comemoração do resgate ---------------- */

// Faíscas do estouro: ângulos distribuídos em volta do prêmio, com distância e
// atraso variando por índice. Valores fixos (e não Math.random) pra a animação
// ser sempre a mesma e não divergir entre renders.
const SPARKS = Array.from({ length: 16 }, (_, i) => ({
  angle: (360 / 16) * i + (i % 2 ? 11 : -7),
  distance: [104, 80, 122][i % 3],
  delay: (i % 5) * 26,
  size: i % 4 === 0 ? 13 : 9,
}));

// A comemoração — pulo do card, virada de cor, carimbo do check e as faíscas
// saindo do centro — só acontece quando o resgate cai com o popup aberto. É a
// PASSAGEM de um estado pro outro; reabrir um degrau já resgatado não repete.
//
// O leque de raios é a exceção: ele não marca a passagem, marca o prêmio, e
// por isso acompanha todo degrau resgatado.
const celebrating = ref(false);

let celebrationTimer: ReturnType<typeof setTimeout> | null = null;

function stopCelebration() {
  if (celebrationTimer) {
    clearTimeout(celebrationTimer);
    celebrationTimer = null;
  }

  celebrating.value = false;
}

watch(
  () => props.reward?.state,
  (state, previous) => {
    if (state !== 'claimed' || previous !== 'claimable') return;

    stopCelebration();
    celebrating.value = true;
    // Desmonta as camadas quando a animação mais longa termina, pra não deixar
    // pseudo-elementos animados pendurados no card.
    celebrationTimer = setTimeout(() => {
      celebrating.value = false;
      celebrationTimer = null;
    }, 1200);
  },
);

onBeforeUnmount(stopCelebration);

watch(
  () => props.reward,
  async (value) => {
    if (value) {
      infoOpen.value = false;
      visible.value = true;
      await nextTick();
      shown.value = true;
    } else {
      shown.value = false;
    }
  },
  { immediate: true },
);

function onClosed() {
  visible.value = false;
  infoOpen.value = false;
  stopCelebration();
}

function close() {
  emit('close');
}

function onClaim() {
  if (!props.reward || props.claiming) return;
  emit('claim');
}

function onUse() {
  if (!ctaLink.value) return;
  emit('use', ctaLink.value);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="dcp-overlay"
      @dragstart.prevent
    >
      <div
        class="dcp-backdrop"
        :class="{ 'dcp-backdrop--out': !shown }"
        @click="close"
      />

      <Transition
        name="dcp-pop"
        @after-leave="onClosed"
      >
        <div
          v-if="shown && reward"
          class="dcp-card"
          :class="[`dcp-card--${tone}`, { 'dcp-card--won': celebrating }]"
        >
          <button
            v-if="hasInfo"
            aria-label="Sobre o prêmio"
            class="dcp-icon dcp-icon--info"
            type="button"
            @click="infoOpen = !infoOpen"
          >
            <img
              alt=""
              :src="infoIcon"
            />
          </button>

          <button
            aria-label="Fechar"
            class="dcp-icon dcp-icon--close"
            type="button"
            @click="close"
          >
            <img
              alt=""
              :src="closeIcon"
            />
          </button>

          <!-- Scrim invisível: fecha o popover ao clicar fora, como na tela do
               calendário. -->
          <div
            v-if="infoOpen"
            class="dcp-info-scrim"
            @click="infoOpen = false"
          />

          <!-- Ficha do prêmio, ancorada no (i). -->
          <Transition name="dcp-fade">
            <div
              v-if="infoOpen"
              class="dcp-info"
            >
              <p
                v-if="reward.title"
                class="dcp-info__name"
                >{{ reward.title }}</p
              >
              <p class="dcp-info__text">{{ reward.description }}</p>
              <button
                class="dcp-info__ok"
                type="button"
                @click="infoOpen = false"
              >
                Entendi
              </button>
            </div>
          </Transition>

          <span class="dcp-badge">{{ reward.position }}º Dia</span>

          <p
            class="dcp-headline"
            :class="{ 'dcp-headline--won': celebrating }"
          >
            {{ headline }}
            <!-- Frase própria, sem concordar com o título: o Figma escreveu
                 "resgatados com sucesso!", que só fecha com título masculino
                 plural ("Baú de Coins resgatados" erra o número, "Rodada
                 Grátis resgatados" erra os dois). O título do prêmio é texto
                 livre do core, então a linha não pode depender dele. -->
            <span
              v-if="isClaimed"
              class="dcp-headline__ok"
              >Resgate concluído com sucesso!</span
            >
          </p>

          <!-- Arte: 160 na visão resumo, 100 quando a descrição entra. -->
          <div
            class="dcp-art"
            :class="{ 'dcp-art--won': celebrating }"
          >
            <!-- Leque de raios: acompanha o prêmio sempre que ele está
                 resgatado, inclusive ao reabrir. -->
            <span
              v-if="isClaimed"
              class="dcp-rays"
            />

            <!-- Faíscas: SÓ na passagem de resgatável pra resgatado. Elas
                 saem do centro, e uma coisa que sai do centro é um começo —
                 repetir isso num card que já estava verde lia como se algo
                 tivesse acontecido de novo. -->
            <template v-if="celebrating">
              <span
                v-for="(spark, i) in SPARKS"
                :key="i"
                class="dcp-spark"
                :style="{
                  '--a': `${spark.angle}deg`,
                  '--dist': `${spark.distance}px`,
                  '--d': `${spark.delay}ms`,
                  '--s': `${spark.size}px`,
                }"
              />
            </template>

            <img
              alt=""
              class="dcp-art__img"
              :src="prizeImage"
            />
            <img
              v-if="isClaimed"
              alt=""
              class="dcp-art__check"
              :class="{ 'dcp-art__check--stamp': celebrating }"
              :src="asset('popup/check.svg')"
            />
          </div>

          <!-- Resgatar: o botão é uma arte do Figma cuja pintura sangra pra
               fora da caixa de 185x50. -->
          <button
            v-if="isClaimable"
            aria-label="Resgatar"
            class="dcp-claim"
            :disabled="claiming"
            type="button"
            @click="onClaim"
          >
            <img
              alt="Resgatar"
              class="dcp-claim__img"
              :src="asset('popup/botao-resgatar.webp')"
            />
            <span
              v-if="claiming"
              class="dcp-claim__loading"
              >...</span
            >
          </button>

          <!-- USAR no degrau resgatado (variante "Resgatado State 01" do
               Figma): leva o jogador ao destino cadastrado no prêmio. Sem
               `cta_url` na resposta o destino é a carteira; com o campo vazio
               o botão não existe — o core está dizendo que este prêmio não
               tem pra onde levar, e um botão que não leva a nada é pior que
               nenhum botão. -->
          <button
            v-else-if="hasUse"
            class="dcp-use"
            type="button"
            @click="onUse"
          >
            USAR
          </button>

          <!-- Recusa do resgate, com a mensagem que a store resolveu pelo
               código de erro do core. -->
          <p
            v-if="error"
            class="dcp-error"
            >{{ error }}</p
          >
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<!-- Sem scope: @keyframes contornam o reset global de transition. -->
<style>
/* Entrada só por opacidade, SEM escala.

   A escala (0.9 → 1) fazia o navegador rasterizar o card numa camada e
   redimensioná-la; ao terminar, a camada é descartada e tudo volta a ser
   desenhado direto na grade de pixels. Esse reencaixe é uma ticada de menos de
   um pixel, mas acontece no card INTEIRO de uma vez — por isso aparecia em
   toda abertura, e não só na imagem.

   Opacidade não mexe em geometria: o card é desenhado na mesma posição do
   primeiro ao último quadro, então não há nada pra reencaixar. */
.dcp-pop-enter-active {
  animation: dcp-pop 0.22s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: opacity;
}
.dcp-pop-leave-active {
  animation: dcp-pop 0.16s ease-in reverse forwards;
  will-change: opacity;
}
@keyframes dcp-pop {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

/* Ficha do prêmio: mesma entrada do popover da tela do calendário. */
.dcp-fade-enter-active {
  animation: dcp-fade 0.16s ease-out forwards;
}
.dcp-fade-leave-active {
  animation: dcp-fade 0.12s ease-in reverse forwards;
}
@keyframes dcp-fade {
  0% {
    transform: translateY(-6px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Acima do calendário (z-9996) — é um diálogo sobre ele.
.dcp-overlay {
  @apply fixed inset-0 z-[9997] flex items-center justify-center select-none;

  padding: 16px;

  img {
    -webkit-user-drag: none;
  }
}

.dcp-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.6);
  animation: dcp-fade-in 0.2s ease forwards;
}

.dcp-backdrop--out {
  animation: dcp-fade-in 0.16s ease reverse forwards;
}

@keyframes dcp-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 250px é a largura do Figma; encolhe só em telas muito estreitas.
.dcp-card {
  @apply relative z-10 flex flex-col items-center;

  // Paleta do popover da ficha, sobrescrita por cada tom logo abaixo, pra ele
  // acompanhar o gradiente do card em vez de ser sempre navy. Default = navy.
  --dcp-pop-bg: #0c1b44;
  --dcp-pop-border: #203570;
  --dcp-pop-accent: #58c0fa;
  --dcp-pop-btn: #203570;

  width: 250px;
  max-width: 100%;
  // Todos os tons na mesma altura, medida no tom RESGATADO, que é o mais alto
  // dos comuns desde que o USAR entrou: 1px de borda + selo 40 + manchete 54 +
  // arte 160 + botão 45 + 3 gaps de 10 + 16 de padding + 1px de borda = 347.

  // A manchete são TRÊS linhas, não duas: o título ocupa uma e a frase verde
  // ("Resgate concluído com sucesso!") não cabe em 218px de largura útil na
  // Plus Jakarta Sans 15/800, então quebra. É por isso que o número não é o 327
  // do frame do Figma — lá a manchete inteira cabia em duas linhas porque era
  // uma frase só, e a nossa é deliberadamente duas (ver o comentário da
  // manchete no template).

  // O resgatável mede 316 por conta própria e a folga vai pras margens
  // automáticas da arte. Sem isso o card CRESCERIA 20px na virada
  // resgatável → resgatado, e mudar de tamanho durante a virada de cor endurece
  // a transição. É `min-height` porque o tom com mensagem de erro é mais alto e
  // continua podendo crescer — e um título que quebre em duas linhas também.
  min-height: 347px;
  // Sem padding no topo: o selo "Nº DIA" encosta na borda de cima.
  padding: 0 16px 16px;
  gap: 10px;
  border: 1px solid;
  border-radius: 16px;
}

/* ---------- selo Nº DIA ---------- */
.dcp-badge {
  @apply flex items-center justify-center shrink-0 text-white text-center;

  // Mínimo do Figma; número de dois dígitos + "º Dia" em 24/800 chega perto dos
  // 102px, então o selo pode crescer em vez de clipar.
  min-width: 102px;
  height: 40px;
  padding-inline: 8.125px;
  border: 1px solid;
  // Só os cantos de baixo — o selo pende da borda superior do card.
  border-radius: 0 0 20px 20px;
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

/* ---------- textos ---------- */
.dcp-headline {
  @apply shrink-0 text-white text-center;

  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
}

.dcp-headline__ok {
  display: block;
  color: #00ff1d;
}

/* ---------- ficha do prêmio (popover do (i)) ---------- */
// Mesmo desenho do popover da tela do calendário (.dc-info): caixa navy com
// barriguinha apontando pro botão e scrim de clique-fora.
.dcp-info-scrim {
  @apply absolute inset-0;

  z-index: 8;
}

.dcp-info {
  position: absolute;
  z-index: 9;
  // 9 (topo do (i)) + 30 (altura) + 8 de folga.
  top: 47px;
  left: 10px;
  width: 200px;
  padding: 10px;
  border: 1px solid var(--dcp-pop-border);
  border-radius: 10px;
  background: var(--dcp-pop-bg);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  text-align: left;

  // Centrada sob o (i): o botão vai de 10 a 40, centro em 25; o popover começa
  // em 10, então o deslocamento é 15 menos metade do quadrado.
  &::before {
    content: '';

    position: absolute;
    top: -6px;
    left: 9px;
    width: 12px;
    height: 12px;
    border-top: 1px solid var(--dcp-pop-border);
    border-left: 1px solid var(--dcp-pop-border);
    border-radius: 3px 0 0 0;
    background: var(--dcp-pop-bg);
    transform: rotate(45deg);
  }
}

// Nome do prêmio no ciano do Figma, que antes era o título da visão de
// detalhe do card.
.dcp-info__name {
  margin-bottom: 4px;
  color: var(--dcp-pop-accent);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
}

.dcp-info__text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  line-height: 1.35;
}

.dcp-info__ok {
  @apply w-full text-white font-bold;

  margin-top: 8px;
  padding: 6px;
  border-radius: 7px;
  background: var(--dcp-pop-btn);
  font-size: 11px;
}

/* ---------- arte ---------- */
// A caixa é 160x160 FIXO em todos os tons, e a folga da altura mínima vai nas
// margens automáticas: o selo continua pendurado na borda de cima, o botão
// continua no rodapé e o que sobra se reparte em volta da arte.

// A caixa NÃO pode esticar. As artes vêm do core em proporções diferentes (o
// baú é 224x288, retrato), e com `object-fit: contain` numa caixa mais alta o
// mesmo prêmio renderiza maior — media 160x206 no card sem botão contra
// 125x161 no card com botão. Tamanho de prêmio não pode depender do tom.
.dcp-art {
  @apply relative flex shrink-0 items-center justify-center;

  width: 160px;
  height: 160px;
  margin-block: auto;
}

// `relative` + z-index só pra fixar a ordem contra as camadas da comemoração:
// os raios (z-0) ficam atrás da arte e as faíscas (z-2) na frente.
.dcp-art__img {
  @apply relative w-full h-full;

  z-index: 1;
  object-fit: contain;
}

// Check do estado resgatado, centrado sobre a arte.
.dcp-art__check {
  @apply absolute;

  z-index: 3;
  left: 50%;
  top: 50%;
  width: 61px;
  height: 52px;
  transform: translate(-50%, -50%);
}

/* ---------- botões ---------- */
// A caixa tem 185x50, mas a pintura do botão sangra pra fora dela (no Figma,
// 116.21% x 143.94% deslocada -8.29% / -21.67%). Por isso o <img> é
// posicionado por fora da caixa em vez de preenchê-la.
.dcp-claim {
  @apply relative shrink-0;

  width: 185px;
  height: 50px;

  &:disabled {
    @apply opacity-70;
  }

  &:not(:disabled):active {
    filter: brightness(0.94);
  }
}

.dcp-claim__img {
  @apply absolute pointer-events-none;

  left: -8.29%;
  top: -21.67%;
  width: 116.21%;
  height: 143.94%;
  max-width: none;
}

.dcp-claim__loading {
  @apply absolute inset-0 flex items-center justify-center text-white font-extrabold;

  font-size: 20px;
}

// USAR: pílula vazada da variante resgatada do Figma (instância "Botão Ver
// Mais", 206x45). O fundo é transparente de propósito — o gradiente verde do
// card é que aparece ali dentro, conferido pixel a pixel no arquivo — e a borda
// é branco a 50%, que é o mesmo branco do texto com a opacidade do Figma.
.dcp-use {
  @apply flex shrink-0 items-center justify-center text-white font-extrabold uppercase;

  width: 206px;
  max-width: 100%;
  height: 45px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  // Pílula: no Figma o raio é metade da altura, então vale arredondar sem conta.
  border-radius: 999px;
  font-size: 14px;
  letter-spacing: 0.4px;

  // Sem `transition` (o reset global mata): o toque acende na hora e apaga na
  // hora, que é o que se espera de um botão de contorno.
  &:active {
    background: rgba(255, 255, 255, 0.12);
  }
}

.dcp-error {
  @apply w-full shrink-0 text-center;

  color: #ffb4a1;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
}

/* ---------- ícones ---------- */
.dcp-icon {
  @apply absolute z-10;

  top: 9px;
  width: 30px;
  height: 30px;

  img {
    @apply w-full h-full;
  }
}

.dcp-icon--info {
  left: 10px;
}

.dcp-icon--close {
  right: 12px;
}

/* ---------- tons ---------- */
// Ângulos de 180.34° no Figma (arredondamento do editor) = 180deg.
// Cada tom leva a paleta do popover junto: o fundo é o extremo escuro do
// próprio gradiente puxado um passo pro claro (pra destacar do card sem virar
// outra cor), a borda é a mesma do card e o botão é a cor do selo.
// Azul do Figma. Chegou a ir pro laranja da célula, pra a cor dizer a mesma
// coisa nos dois lados, e o Lucas preferiu o azul de volta aqui.
.dcp-card--claimable {
  --dcp-pop-bg: #0b0b52;
  --dcp-pop-border: #1b54cf;
  --dcp-pop-accent: #58c0fa;
  --dcp-pop-btn: #1414dd;

  border-color: #1b54cf;
  background: linear-gradient(180deg, #060642 32.836%, #0f0fa8 99.762%);
  filter: drop-shadow(0 0 10px #1414dd);

  .dcp-badge {
    border-color: #1b54cf;
    background: #1414dd;
  }
}

// Mesmo azul do resgatável (decisão do Lucas): o degrau que ainda vem não é
// um estado de erro nem de espera, é só a vez que não chegou — então o card é
// o mesmo. O que separa os dois é o HALO e o botão: só o resgatável brilha e
// só ele tem RESGATAR.
.dcp-card--locked {
  --dcp-pop-bg: #0b0b52;
  --dcp-pop-border: #1b54cf;
  --dcp-pop-accent: #58c0fa;
  --dcp-pop-btn: #1414dd;

  border-color: #1b54cf;
  background: linear-gradient(180deg, #060642 32.836%, #0f0fa8 99.762%);

  .dcp-badge {
    border-color: #1b54cf;
    background: #1414dd;
  }
}

.dcp-card--claimed {
  // O verde do card é claro, então o popover vai no verde escuro do topo do
  // gradiente — é o que mantém o texto branco legível.
  --dcp-pop-bg: #00420a;
  --dcp-pop-border: #17c964;
  --dcp-pop-accent: #7bff8f;
  --dcp-pop-btn: #009d12;

  border-color: #009d12;
  background: linear-gradient(to top, #009d12 77.027%, #003c07 87.838%);

  .dcp-badge {
    border-color: #17c964;
    background: #009d12;
  }
}

/* ---------- comemoração do resgate ---------- */

// A troca de tom é instantânea no DOM (a classe pula de --claimable pra
// --claimed) e o reset global de transition impede o caminho normal. Então a
// animação FAZ o papel da transição: o quadro 0% repinta o card de azul e ele
// desliza até o verde, que é exatamente o valor estático do tom resgatado — no
// fim da animação nada muda de lugar, o CSS assume sem emenda.

// Junto vai o pulinho, numa animação separada: o pulo (transform) precisa da
// curva adiantada pra ter impacto e a virada de cor precisa do contrário — na
// mesma curva do pulo, a cor já chegava no verde aos 120ms.

// Cor sólida (borda, selo) o navegador interpola. GRADIENTE não: o Chromium
// troca `background-image` de forma DISCRETA, no meio da animação — era isso
// que dava o corte seco de azul pra verde enquanto o selo atravessava liso.
// Por isso o gradiente antigo vira uma CAMADA por cima do novo, que se apaga:
// o cross-fade passa a ser de opacidade, que interpola. O `z-index: -1` mantém
// a camada entre o fundo do card e o conteúdo, senão ela cobriria o selo e a
// manchete.

// NADA na comemoração mexe no CONTORNO do card, e isso é regra, não acaso.
// Já saíram daqui, um por um: o `filter: drop-shadow` (que ainda por cima
// re-rasterizava a subárvore inteira a cada quadro), o `scale` do card (1px de
// borda escalado é 1px mudando de espessura na frente do olho) e o halo que
// abria pra fora. Todos liam como a borda se mexendo, e borda se mexendo fica
// deselegante. O que celebra é o miolo: a cor atravessando, o solavanco da
// arte, o carimbo do check e as faíscas.

// `isolation: isolate` é obrigatório aqui: era o `filter` que criava o contexto
// de empilhamento, e sem contexto o `z-index: -1` do ::before cai atrás do
// fundo do card — o cross-fade simplesmente sumiria.
.dcp-card--won {
  isolation: isolate;
  // A borda vai em `ease-in-out` e um pouco mais longa que o fundo: ela é uma
  // linha de 1px, então qualquer trecho rápido da curva vira um corte visível.
  // Ficar um respiro atrás do fundo lê como suavidade, não como atraso.
  animation: dcp-won-tone 780ms ease-in-out;

  // Camada do gradiente antigo, que se apaga.
  &::before {
    content: '';

    position: absolute;
    z-index: -1;
    inset: 0;
    // 15 e não `inherit`: o card tem raio 16 e borda 1, e a caixa de
    // preenchimento (onde o `inset: 0` ancora) tem o raio interno. Com 16 a
    // camada azul escapava 1px na curva das quinas.
    border-radius: 15px;
    background: linear-gradient(180deg, #060642 32.836%, #0f0fa8 99.762%);
    will-change: opacity;
    animation: dcp-won-fade 600ms ease-out forwards;
  }
}

@keyframes dcp-won-fade {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

// Só a borda — o fundo é o cross-fade acima. O `filter` NÃO entra aqui: além
// de caro, keyframe vence declaração normal, então ele anulava o `filter: none`
// do card e voltava de estalo quando a classe saía.
@keyframes dcp-won-tone {
  0% {
    border-color: #1b54cf;
  }

  100% {
    border-color: #009d12;
  }
}

// O selo atravessa junto, senão ele salta pro verde sozinho enquanto o resto
// do card ainda está no meio do caminho.
.dcp-card--won .dcp-badge {
  animation: dcp-won-badge 780ms ease-in-out;
}

@keyframes dcp-won-badge {
  0% {
    border-color: #1b54cf;
    background: #1414dd;
  }

  100% {
    border-color: #17c964;
    background: #009d12;
  }
}

// Leque de raios atrás do prêmio. A máscara radial apaga o centro (pra não
// cobrir a arte) e as pontas, então o leque não termina num círculo duro.
.dcp-rays {
  position: absolute;
  z-index: 0;
  left: 50%;
  top: 50%;
  width: 300px;
  height: 300px;
  background: repeating-conic-gradient(
    rgba(255, 255, 255, 0.62) 0deg 5deg,
    rgba(255, 255, 255, 0) 5deg 21deg
  );
  mask-image: radial-gradient(
    closest-side,
    transparent 14%,
    #000 34%,
    transparent 74%
  );
  -webkit-mask-image: radial-gradient(
    closest-side,
    transparent 14%,
    #000 34%,
    transparent 74%
  );
  pointer-events: none;
  will-change: transform, opacity;
  // Duas animações, cada uma na SUA propriedade, pra não se sobrescreverem: a
  // entrada cuida da opacidade e o giro cuida do transform, pra sempre. Uma só
  // de cada, IGUAL nos dois momentos (resgate e reabertura) — animação que
  // depende de estado troca quando o estado troca, e a troca sempre aparece
  // como piscada.
  animation:
    dcp-rays-in 500ms ease-out both,
    dcp-rays-spin 34s linear infinite;
}

// Sobe direto até o brilho de fundo, sem passar por um pico. O pico era o
// clarão do estouro, mas como a animação é a mesma na reabertura, ali ele
// virava um lampejo em cima da arte e do botão USAR — o leque tem 300px e
// cobre os dois.
@keyframes dcp-rays-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 0.4;
  }
}

@keyframes dcp-rays-spin {
  0% {
    transform: translate(-50%, -50%) scale(0.82) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -50%) scale(0.82) rotate(360deg);
  }
}

// Faíscas: `rotate` e DEPOIS `translateX` é intencional aqui — cada faísca sai
// pelo eixo já girado, que é o que produz o estouro radial.
.dcp-spark {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 50%;
  width: var(--s);
  height: var(--s);
  border-radius: 50%;
  background: radial-gradient(circle, #fff 38%, #d6ffdd 62%, transparent 72%);
  // O halo é o que faz a faísca ler sobre o verde do card.
  box-shadow:
    0 0 8px rgba(255, 255, 255, 0.95),
    0 0 18px rgba(120, 255, 150, 0.75);
  pointer-events: none;
  will-change: transform, opacity;
  animation: dcp-spark 820ms cubic-bezier(0.15, 0.7, 0.3, 1) var(--d) both;
}

// Uma passagem só, sem looping: sai do centro, chega na ponta e apaga.
@keyframes dcp-spark {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--a)) translateX(0) scale(0.4);
  }

  18% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--a)) translateX(var(--dist))
      scale(0.55);
  }
}

// A arte recebe o prêmio com um solavanco.
.dcp-art--won .dcp-art__img {
  animation: dcp-won-art 620ms cubic-bezier(0.2, 0.8, 0.3, 1);
}

@keyframes dcp-won-art {
  0% {
    transform: scale(0.8);
  }

  50% {
    transform: scale(1.12);
  }

  100% {
    transform: scale(1);
  }
}

// Check batendo como carimbo, com um giro curto pra assentar.
.dcp-art__check--stamp {
  animation: dcp-stamp 560ms cubic-bezier(0.2, 0.9, 0.3, 1) 120ms both;
}

@keyframes dcp-stamp {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(2.6) rotate(-16deg);
  }

  55% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(0.92) rotate(4deg);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) rotate(0deg);
  }
}

// A linha verde da manchete é o único conteúdo que nasce com o resgate. Entra
// por cima da virada de cor, não junto com o pulo: mais lenta, com
// deslocamento curto, pra ler como coisa que aparece e não como coisa que
// salta.
.dcp-headline--won {
  animation: dcp-rise 560ms ease-out 300ms both;
}

@keyframes dcp-rise {
  0% {
    opacity: 0;
    transform: translateY(4px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
